/**
 * Family Screen
 *
 * Displays family members as enhanced profile cards with full info.
 * Tap a card to navigate directly to the edit page.
 * Create new members via the add button which navigates to create page.
 */

import { useCallback, useState, useMemo, useEffect } from 'react';
import {
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { FamilyMemberList } from '@/components/family/FamilyMemberList';
import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useFamilyMembers } from '@/hooks/use-family-members';
import { useFamilyCalendarCounts } from '@/hooks/use-family-calendar-counts';
import { useMeProfile } from '@/hooks/use-me-profile';
import { FamilyMemberEvents } from '@/lib/family/FamilyMemberEvents';
import type { FamilyMember } from '@/types/family-member';

export default function FamilyScreen() {
  const router = useRouter();
  const { from } = useLocalSearchParams<{ from?: string }>();
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  // Family members state
  const {
    members,
    isLoading,
    error: membersError,
    refresh: refreshMembers,
  } = useFamilyMembers();

  // Get member IDs for calendar counts
  const memberIds = useMemo(() => members.map(m => m.id), [members]);

  // Calendar counts for each member
  const { getCount, refresh: refreshCounts } = useFamilyCalendarCounts(memberIds);

  // Profile picture for "Me" card
  const { refresh: refreshMeProfile } = useMeProfile();

  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await Promise.all([refreshMembers(), refreshCounts(), refreshMeProfile()]);
    setIsRefreshing(false);
  }, [refreshMembers, refreshCounts, refreshMeProfile]);

  // Navigate to create page
  const handleAddMember = useCallback(() => {
    router.push('/family-member/create');
  }, [router]);

  // Navigate to edit page directly
  const handleMemberPress = useCallback((member: FamilyMember) => {
    router.push(`/family-member/${member.id}/edit`);
  }, [router]);

  // Navigate to edit-profile for "Me" member
  const handleMePress = useCallback(() => {
    router.push('/edit-profile');
  }, [router]);

  // Return to the tab that opened this screen; calendar remains the default
  // for entry paths that carry no param (e.g. post-edit replace).
  const handleBack = useCallback(() => {
    if (from === 'home') {
      router.navigate('/(tabs)');
      return;
    }
    router.navigate('/(tabs)/calendar');
  }, [from, router]);

  // Subscribe to family member changes for automatic refresh
  useEffect(() => {
    const unsubscribe = FamilyMemberEvents.subscribe(() => {
      refreshMembers();
      refreshCounts();
    });
    return unsubscribe;
  }, [refreshMembers, refreshCounts]);

  // Refresh profile picture when screen gains focus (e.g., after editing profile)
  useFocusEffect(
    useCallback(() => {
      refreshMeProfile();
    }, [refreshMeProfile])
  );

  // Error state
  if (membersError) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: palette.background }]}>
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={48} color={palette.textMuted} />
          <Text style={[styles.errorText, { color: palette.text, fontFamily: getBodyFont() }]}>
            {membersError}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: palette.background }]}
      edges={['top']}
    >
      {/* Content */}
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={palette.primary}
          />
        }
      >
        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={palette.primary} />
          </View>
        ) : (
          <FamilyMemberList
            members={members}
            getCalendarCount={getCount}
            onMemberPress={handleMemberPress}
            onMePress={handleMePress}
            onAddPress={handleAddMember}
            onBack={handleBack}
          />
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: Spacing.lg,
    paddingTop: Spacing.xxl,
    paddingBottom: 100, // intentionally large for tab bar clearance
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64, // intentionally large for visual centering
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.xxxl,
  },
  errorText: {
    fontSize: 14,
    textAlign: 'center',
    marginTop: Spacing.lg,
  },
});
